/* eslint-disable react/prop-types */
// @ts-nocheck
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { useSelector } from 'react-redux';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {
  ReactMuiTableColumnHeaderTextEllipsis,
  ReactMuiTableV8,
} from '@nuva-org/solytics-frontend';

import { EMPTY_OBJECT, NOOP } from '../../../../const/CommonConst';
import NoDataPresentComponent from '../../NoDataPresentComponent';
import SwalToast from '../../swalToast';
import {
  createDynamicColumns,
  downloadBlobResponse,
  getTableColumns,
  isValueEmpty,
} from '../../../../utils/Utils';
import { useInventoryViewListQuery } from '../../../../queries/useConfigQueries';
import useEntity from '../../../../hooks/Configuration/useEntity';
import { useDialogFullscreen } from '../../../../hooks/useDialogFullscreen';

const ITEM_HEIGHT_FOR_VIRTUALIZATION = 30;

const normalizeSavedSelections = (value) => {
  if (!Array.isArray(value)) {
    return EMPTY_OBJECT;
  }

  return value.reduce((accumulator, item) => {
    const savedEntityId = item?.entity_id;

    if (isValueEmpty(savedEntityId)) {
      return accumulator;
    }

    return {
      ...accumulator,
      [savedEntityId]: {
        ...item,
        checked: false, // as value will contain only excluded rows, we can treat checked as false by default
      },
    };
  }, {});
};

const InventoryViewCheckboxCell = memo(
  ({
    checked = false,
    disabled = false,
    label = 'inventory view row',
    onChange = NOOP,
  }) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        <Checkbox
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          size="small"
          inputProps={{
            'aria-label': `Select ${label}`,
          }}
        />
      </Box>
    );
  }
);

const InventoryViewRemarkCell = memo(
  ({
    disabled = false,
    error = false,
    label = 'inventory view row',
    onClick = NOOP,
  }) => {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        title={disabled ? 'Not available' : 'View exclusion remark'}
      >
        <IconButton
          aria-label={`Open exclusion remark for ${label}`}
          onClick={onClick}
          disabled={disabled}
        >
          <CommentIcon color={error ? 'error' : 'inherit'} />
        </IconButton>
      </Box>
    );
  }
);

const InventoryViewRemarkDialog = memo(
  ({
    open = false,
    rowLabel = 'Inventory View',
    value = '',
    disabled = false,
    onClose = NOOP,
    onSave = NOOP,
  }) => {
    const [remarkDraft, setRemarkDraft] = useState('');

    useEffect(() => {
      setRemarkDraft(value || '');
    }, [value]);

    const handleRemarkDraftChange = useCallback(
      (event) => {
        const nextValue = event?.target?.value || '';

        setRemarkDraft(nextValue);
      },
      [setRemarkDraft]
    );

    const isRemarkDialogError = useMemo(() => {
      return !disabled && isValueEmpty(remarkDraft?.trim());
    }, [disabled, remarkDraft]);

    const handleSaveRemark = useCallback(() => {
      onSave(remarkDraft);
    }, [onSave, remarkDraft]);

    return (
      <Dialog
        onClose={onClose}
        open={open}
        maxWidth="sm"
        fullWidth
        aria-labelledby="inventory-view-remark-title"
      >
        <DialogTitle id="inventory-view-remark-title">
          {`Exclusion Remark${rowLabel ? `: ${rowLabel}` : ''}`}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={remarkDraft}
            disabled={disabled}
            error={isRemarkDialogError}
            onChange={handleRemarkDraftChange}
            placeholder="Enter exclusion remark"
            helperText={
              isRemarkDialogError ? 'Exclusion remark is required' : ' '
            }
            inputProps={{
              'aria-label': `Exclusion remark for ${rowLabel}`,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {disabled ? 'Close' : 'Cancel'}
          </Button>
          {!disabled && (
            <Button
              variant="contained"
              onClick={handleSaveRemark}
              disabled={isRemarkDialogError}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

const InventoryViewAttribute = ({
  label,
  showLabel = true,
  mode = 'edit',
  attribute = EMPTY_OBJECT,
  onChange = NOOP,
  entityDetailsFormikForm = EMPTY_OBJECT,
  entityId = '',
  entityType = '',
  value,
}) => {
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isRemarkDialogOpen, setIsRemarkDialogOpen] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [activeRemarkRow, setActiveRemarkRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState({});
  const { userData } = useSelector((state) => state.users);
  const { downloadInventoryViewReport } = useEntity();

  const { isFullScreen, toggleFullScreen, exitFullScreen } =
    useDialogFullscreen();

  const isLinkedInventoryViewAttribute = useMemo(() => {
    return attribute?.is_inventory_view_attribute_linked;
  }, [attribute?.is_inventory_view_attribute_linked]);

  const linkedInventoryViewConfig = useMemo(() => {
    return attribute?.linked_inventory_view || null;
  }, [attribute?.linked_inventory_view]);

  const { linkedAttributeId, rawLinkedValue } = useMemo(() => {
    return {
      linkedAttributeId: linkedInventoryViewConfig?.attribute_id,
      rawLinkedValue:
        entityDetailsFormikForm?.values?.[
          linkedInventoryViewConfig?.attribute_id
        ],
    };
  }, [linkedInventoryViewConfig, entityDetailsFormikForm?.values]);

  const {
    data: inventoryViewData = EMPTY_OBJECT,
    isFetching,
    fetchInventoryViews,
  } = useInventoryViewListQuery({
    enabled: false,
    params: {
      entity_id: entityId,
      entity_type: entityType,
      // send attribute id when it is not linked to inventory view attribute,
      // when it is linked to inventory view attribute, then pass the linked attribute id
      // as data should be fetched on the basis of linked attribute id in that case
      attribute_id: isLinkedInventoryViewAttribute
        ? linkedAttributeId
        : attribute?.id,
      form_data: entityDetailsFormikForm?.values || EMPTY_OBJECT,
    },
  });

  useEffect(() => {
    if (!isLinkedInventoryViewAttribute) {
      setSelectedRows(normalizeSavedSelections(value));
      return;
    }
    if (isValueEmpty(linkedAttributeId) || !Array.isArray(rawLinkedValue)) {
      return;
    }
    setSelectedRows(normalizeSavedSelections(rawLinkedValue));
  }, [
    value,
    linkedAttributeId,
    rawLinkedValue,
    isLinkedInventoryViewAttribute,
  ]);

  const { isLinkedExcludedMode, isLinkedIncludedMode } = useMemo(() => {
    return {
      isLinkedExcludedMode:
        isLinkedInventoryViewAttribute &&
        linkedInventoryViewConfig?.mode === 'excluded',
      isLinkedIncludedMode:
        isLinkedInventoryViewAttribute &&
        linkedInventoryViewConfig?.mode === 'included',
    };
  }, [isLinkedInventoryViewAttribute, linkedInventoryViewConfig]);

  const isViewMode = useMemo(
    () => mode === 'view' || isLinkedInventoryViewAttribute,
    [mode, isLinkedInventoryViewAttribute]
  );

  const tableData = useMemo(() => {
    const allRows = inventoryViewData?.data || [];
    const selectedRowIds = new Set(Object.keys(selectedRows));

    if (isLinkedExcludedMode) {
      // If the attribute is linked to an inventory view attribute in "excluded" mode,
      // we show the de-selected rows which are present in the linked attribute value map, as those are the ones that are excluded
      return allRows.filter((row) => selectedRowIds.has(row?.entity_id));
    }
    if (isLinkedIncludedMode) {
      // If the attribute is linked to an inventory view attribute in "included" mode,
      // we show the selected rows which are present in the linked attribute value map, as those are the ones that are included
      return allRows.filter((row) => !selectedRowIds.has(row?.entity_id));
    }
    return allRows;
  }, [
    inventoryViewData?.data,
    isLinkedExcludedMode,
    selectedRows,
    isLinkedIncludedMode,
  ]);

  const buttonTitle = useMemo(() => {
    /**
     * If the mode is 'view' or the attribute is already linked to an inventory view attribute,
     * show "View {Attribute Label}"
     */
    return isViewMode
      ? `View ${attribute?.label || 'Inventory View'}`
      : `Manage ${attribute?.label || 'Inventory View'}`;
  }, [isViewMode, attribute?.label]);

  const handleRowSelectionChange = useCallback(
    (rowEntityId, checked) => {
      setSelectedRows((prev) => ({
        ...prev,
        [rowEntityId]: {
          checked,
          // If user checks the row, we can clear the exclusion remark and remarked_by as those are only relevant when a row is excluded
          exclusion_remark: '',
          remarked_by: '',
        },
      }));
    },
    [setSelectedRows]
  );

  const handleRemarkChange = useCallback(
    (rowEntityId, exclusionRemark) => {
      setSelectedRows((prev) => ({
        ...prev,
        [rowEntityId]: {
          ...prev?.[rowEntityId],
          exclusion_remark: exclusionRemark.trim() || '',
          remarked_by: userData?.username || '',
        },
      }));
    },
    [setSelectedRows, userData?.username]
  );

  const handleOpenRemarkDialog = useCallback(
    (rowData) => {
      setActiveRemarkRow(rowData);
      setIsRemarkDialogOpen(true);
    },
    [setActiveRemarkRow, setIsRemarkDialogOpen]
  );

  const handleCloseRemarkDialog = useCallback(() => {
    setIsRemarkDialogOpen(false);
    setActiveRemarkRow(null);
  }, [setIsRemarkDialogOpen, setActiveRemarkRow]);

  const handleSaveRemarkDialog = useCallback(
    (nextRemark) => {
      if (isValueEmpty(activeRemarkRow?.entity_id)) {
        return;
      }

      handleRemarkChange(activeRemarkRow?.entity_id, nextRemark);
      handleCloseRemarkDialog();
    },
    [activeRemarkRow?.entity_id, handleCloseRemarkDialog, handleRemarkChange]
  );

  const getRemarkDialogOpenHandler = useCallback(
    (rowData) => {
      return () => {
        handleOpenRemarkDialog(rowData);
      };
    },
    [handleOpenRemarkDialog]
  );

  const getRowSelectionChangeHandler = useCallback(
    (rowEntityId) => {
      return (event) => {
        handleRowSelectionChange(rowEntityId, event?.target?.checked);
      };
    },
    [handleRowSelectionChange]
  );

  const { tableColumns } = useMemo(() => {
    const refinedColumns = getTableColumns(inventoryViewData?.columns || []);
    const columns = createDynamicColumns({
      data: refinedColumns,
      width: 150,
      HeaderCell: ReactMuiTableColumnHeaderTextEllipsis,
    });

    if (isLinkedExcludedMode) {
      const exclusionRemarkColumn = {
        heading: 'Exclusion Remark',
        Header: 'Exclusion Remark',
        accessor: '_inventory_view_exclusion_remark',
        disableSortBy: true,
        width: 160,
        disableOrdering: true,
        Cell: ({ row }) => {
          return (
            <InventoryViewRemarkCell
              label={row?.original?.entity_alias || row?.original?.entity_name}
              onClick={getRemarkDialogOpenHandler(row?.original)}
            />
          );
        },
      };

      const remarkedByColumn = {
        heading: 'Remarked By',
        Header: 'Remarked By',
        accessor: '_inventory_view_remarked_by',
        disableSortBy: true,
        width: 160,
        disableOrdering: true,
        Cell: ({ row }) => {
          const rowEntityId = row?.original?.entity_id;
          const remarkedBy = selectedRows?.[rowEntityId]?.remarked_by;

          return <Typography variant="body2">{remarkedBy || '-'}</Typography>;
        },
      };

      return {
        tableColumns: [...columns, exclusionRemarkColumn, remarkedByColumn],
      };
    }
    if (isLinkedIncludedMode) {
      return {
        tableColumns: columns,
      };
    }

    const checkboxColumn = {
      heading: 'Checkbox',
      Header: 'Selection',
      accessor: '_inventory_view_checkbox',
      disableSortBy: true,
      width: 60,
      disableOrdering: true,
      Cell: ({ row }) => {
        const rowEntityId = row?.original?.entity_id;
        return (
          <InventoryViewCheckboxCell
            checked={selectedRows?.[rowEntityId]?.checked ?? true}
            disabled={isViewMode}
            label={row?.original?.entity_alias || row?.original?.entity_name}
            onChange={getRowSelectionChangeHandler(rowEntityId)}
          />
        );
      },
    };

    const exclusionRemarkColumn = {
      heading: 'Exclusion Remark',
      Header: 'Exclusion Remark',
      accessor: '_inventory_view_exclusion_remark',
      disableSortBy: true,
      width: 160,
      disableOrdering: true,
      Cell: ({ row }) => {
        const rowEntityId = row?.original?.entity_id;
        const isChecked = selectedRows?.[rowEntityId]?.checked ?? true;
        const hasRemarkError =
          !isChecked &&
          isValueEmpty(selectedRows?.[rowEntityId]?.exclusion_remark);

        return (
          <InventoryViewRemarkCell
            disabled={isChecked}
            label={row?.original?.entity_alias || row?.original?.entity_name}
            error={hasRemarkError}
            onClick={getRemarkDialogOpenHandler(row?.original)}
          />
        );
      },
    };

    const remarkedByColumn = {
      heading: 'Remarked By',
      Header: 'Remarked By',
      accessor: '_inventory_view_remarked_by',
      disableSortBy: true,
      width: 140,
      disableOrdering: true,
      Cell: ({ row }) => {
        const rowEntityId = row?.original?.entity_id;
        const remarkedBy = selectedRows?.[rowEntityId]?.remarked_by;

        return <Typography variant="body2">{remarkedBy || '-'}</Typography>;
      },
    };

    return {
      tableColumns: [
        checkboxColumn,
        exclusionRemarkColumn,
        remarkedByColumn,
        ...columns,
      ],
    };
  }, [
    getRemarkDialogOpenHandler,
    getRowSelectionChangeHandler,
    handleRowSelectionChange,
    inventoryViewData?.columns,
    isLinkedExcludedMode,
    isLinkedIncludedMode,
    isViewMode,
    selectedRows,
  ]);

  const isUpdateDisabled = useMemo(() => {
    return tableData.some((row) => {
      const rowSelection = selectedRows?.[row?.entity_id];

      if ((rowSelection?.checked ?? true) === true) {
        return false;
      }

      return isValueEmpty(rowSelection?.exclusion_remark?.trim());
    });
  }, [selectedRows, tableData]);

  const getExcludedEntitiesPayload = useCallback(() => {
    return Object.entries(selectedRows).reduce(
      (accumulator, [rowEntityId, rowSelection]) => {
        if ((rowSelection?.checked ?? false) === true) {
          return accumulator;
        }

        accumulator.push({
          entity_id: rowEntityId,
          exclusion_remark: rowSelection?.exclusion_remark || '',
          remarked_by: rowSelection?.remarked_by || '',
        });

        return accumulator;
      },
      []
    );
  }, [selectedRows]);

  const maxHeightForVirtualization = useMemo(() => {
    return ITEM_HEIGHT_FOR_VIRTUALIZATION * tableData.length >=
      document.documentElement.clientHeight * 0.45
      ? document.documentElement.clientHeight * 0.45
      : ITEM_HEIGHT_FOR_VIRTUALIZATION * tableData.length;
  }, [tableData, document.documentElement.clientHeight]);

  const handleGetColumnProps = useCallback(() => {
    return {
      style: {
        textAlign: 'left',
        textOverflow: 'ellipsis',
        overflow: ' hidden',
        wordBreak: 'initial',
        whiteSpace: 'nowrap',
        paddingLeft: 10,
      },
    };
  }, []);

  const handleGetHeaderProps = useCallback(() => {
    return {
      style: {
        display: 'flex',
        alignItems: 'center',
      },
    };
  }, []);

  const handleFieldClick = useCallback(async () => {
    setIsActionModalOpen(true);
    fetchInventoryViews();
  }, [fetchInventoryViews, setIsActionModalOpen]);

  const handleCloseActionModal = useCallback(() => {
    setIsActionModalOpen(false);

    // on closing of modal, we can reset the selected rows to the saved value to discard any unsaved changes made by user
    // if attribute is not linked to inventory view attribute, we will reset the value on the basis of value prop
    if (!isLinkedInventoryViewAttribute) {
      setSelectedRows(normalizeSavedSelections(value));
      return;
    }
    // if attribute is linked to inventory view attribute, we will reset the value with rawLinkedValue(value of linked attribute)
    if (isValueEmpty(linkedAttributeId) || !Array.isArray(rawLinkedValue)) {
      setSelectedRows({});
      return;
    }

    setSelectedRows(normalizeSavedSelections(rawLinkedValue));
  }, [
    isLinkedInventoryViewAttribute,
    linkedAttributeId,
    rawLinkedValue,
    setIsActionModalOpen,
    setSelectedRows,
    value,
  ]);

  const handleDownload = useCallback(() => {
    const excludedEntitiesPayload = getExcludedEntitiesPayload();
    const payload = {
      entity_id: entityId,
      entity_type: entityType,
      attribute_id: attribute?.id,
      excluded_entity: excludedEntitiesPayload,
      form_data: entityDetailsFormikForm?.values || EMPTY_OBJECT,
    };

    setIsDownloadLoading(true);
    downloadInventoryViewReport(payload)
      .then((response) => {
        if (response) {
          const fileName = `${attribute?.label || 'inventory_view_report'}.xlsx`;
          downloadBlobResponse(response, fileName);
        }
      })
      .catch(() => {
        SwalToast({
          icon: 'error',
          title: 'Unable to download inventory view report.',
        });
      })
      .finally(() => {
        setIsDownloadLoading(false);
      });
  }, [
    attribute,
    setIsDownloadLoading,
    downloadInventoryViewReport,
    entityId,
    entityType,
    getExcludedEntitiesPayload,
    entityDetailsFormikForm?.values,
  ]);

  const handleSave = useCallback(() => {
    const excludedEntitiesPayload = getExcludedEntitiesPayload();

    onChange(excludedEntitiesPayload);
    setIsActionModalOpen(false);
  }, [getExcludedEntitiesPayload, onChange, setIsActionModalOpen]);

  const renderInventoryViewContent = useCallback(() => {
    const tableContainerHeight = isFullScreen ? 'calc(100vh - 220px)' : '100%';

    if (isFetching) {
      return (
        <Box
          sx={{
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (!tableData?.length || !tableColumns?.length) {
      return (
        <Box
          sx={{
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NoDataPresentComponent message="No inventory view data found." />
        </Box>
      );
    }

    return (
      <ReactMuiTableV8
        data={tableData}
        columns={tableColumns}
        initialHiddenColumns={[]}
        initialColumnOrder={[]}
        getHeaderProps={handleGetHeaderProps}
        getColumnProps={handleGetColumnProps}
        stickyHeader={true}
        stickyHeaderConfig={{
          containerStyle: {
            height: tableContainerHeight,
            ...(isFullScreen ? { maxHeight: tableContainerHeight } : {}),
            overflow: 'auto',
            width: 'auto',
          },
          tableHeadStyle: { zIndex: 1 },
        }}
        enableRowSelection={false}
        pageCount={tableData?.length}
        initialPageSize={10}
        rowsPerPageOptions={[10, 15, 25]}
        showOptions={false}
        showToolbar={false}
        enableVirtualization={false}
        isAllOptionDisabled={true}
        maxHeightForVirtualization={maxHeightForVirtualization}
        itemHeightForVirtualization={ITEM_HEIGHT_FOR_VIRTUALIZATION}
        noSearchResultMessage="No inventory view data found."
      />
    );
  }, [
    handleGetColumnProps,
    handleGetHeaderProps,
    isFullScreen,
    isFetching,
    maxHeightForVirtualization,
    tableColumns,
    tableData,
  ]);

  return (
    <Box>
      {showLabel && (
        <Typography
          variant="body4"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            maxWidth: '100%',
            ':hover': {
              cursor: 'pointer',
            },
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        onClick={handleFieldClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '40px',
          cursor: 'pointer',
        }}
      >
        <Typography
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            fontSize: '14px',
          }}
        >
          {buttonTitle}
        </Typography>
      </Box>

      <Dialog
        open={isActionModalOpen}
        onClose={handleCloseActionModal}
        fullWidth
        maxWidth={isFullScreen ? false : 'lg'}
        fullScreen={isFullScreen}
        TransitionProps={{
          onExited: () => exitFullScreen(),
        }}
        PaperProps={{
          sx: {
            maxHeight: isFullScreen ? '100vh' : '90vh',
            height: isFullScreen ? '100vh' : 'auto',
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">{`${attribute?.label || 'Inventory View'}`}</Typography>
          <Box display="flex" alignItems="center">
            {/* Fullscreen Button */}
            <Tooltip title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}>
              <IconButton
                color="primary"
                aria-label={
                  isFullScreen ? 'Exit full screen' : 'Enter full screen'
                }
                onClick={toggleFullScreen}
              >
                {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            {/* Close Button */}
            <IconButton
              color="primary"
              aria-label="close"
              onClick={handleCloseActionModal}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>{renderInventoryViewContent()}</DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={handleDownload}
            startIcon={<DownloadOutlinedIcon />}
            disabled={isFetching || isDownloadLoading}
          >
            {isDownloadLoading ? 'Downloading...' : 'Download'}
          </Button>
          <Button variant="outlined" onClick={handleCloseActionModal}>
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          {!isViewMode && (
            <Button
              variant="contained"
              onClick={handleSave}
              color="primary"
              disabled={isUpdateDisabled}
            >
              Update
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {isRemarkDialogOpen && (
        <InventoryViewRemarkDialog
          open={isRemarkDialogOpen}
          rowLabel={
            activeRemarkRow?.entity_alias || activeRemarkRow?.entity_name
          }
          value={
            selectedRows?.[activeRemarkRow?.entity_id]?.exclusion_remark || ''
          }
          disabled={isViewMode}
          onClose={handleCloseRemarkDialog}
          onSave={handleSaveRemarkDialog}
        />
      )}
    </Box>
  );
};

export default InventoryViewAttribute;
